import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cookies must ride along with every request for the session to work, and
// a 401 from ANY endpoint (every /api/* route except /auth/me requires a
// session - see backend/auth_service.py's auth_middleware) always means
// "not signed in" in this app, so sending the user straight to Microsoft
// login is the right global behavior rather than a dead-end error toast.
axios.defaults.withCredentials = true;
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Oct 26 2026: shop-floor users sign in with a username, so a 401 can no
    // longer be thrown straight at Microsoft - the app's own login screen
    // offers both. The password sign-in call handles its own 401 inline.
    if (error.response?.status === 401 && !String(error.config?.url || "").includes("/auth/password-login")) {
      window.location.href = "/";
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

// Every page in the app's nav that IT can independently grant/revoke on the
// Access Management page - kept in sync with backend/auth_service.py's
// PAGE_CATALOG (labels only live here; the source of truth for WHICH pages
// exist and what backend routes they gate is the backend).
export const PAGE_LABELS = {
  bom_explorer: "BOM Management",
  production_plan: "Production Planning",
  production_confirmation: "Production Confirmation",
  // Sep 2026, user's explicit ask: the admin-only "test" variant is now
  // its own grantable right.
  production_confirmation_test: "Production Confirmation (Test)",
  inventory: "Stock Overview",
  // Aug 27 2026, user's explicit ask: split out of "inventory" into its
  // own grantable right.
  stock_transfer: "Inter Plant Stock Transfer",
  // Sep 3 2026, user's explicit ask: split out of "anyUser" into its own
  // grantable right (was previously open to any logged-in user).
  inbound_stock_transfer: "Inbound STO Receipt",
  admin: "Component Master (Admin)",
  admin_sap_write: "SAP Write (Admin)",
  store_approval: "Store Goods Issue",
  // Sep 24 2026, user's explicit ask: read-only cross-cutting ledger of
  // every SAP Goods Movement across STO Relocation, Store Issue, and
  // Store Return.
  movement_audit_trail: "Movement Audit Trail (All Users)",
  // Sep 2026, user's explicit ask: "Copy Existing BOM" needs its own
  // assignable right (was already gated server-side in PAGE_CATALOG/
  // PAGE_ROUTE_RULES, but missing here meant it never rendered as a
  // checkbox on the Access Management page - fixed this session).
  admin_bom_copy: "Copy Existing BOM",
  // Sep 29 2026, user's explicit ask: same latent bug as admin_bom_copy
  // above - already gated server-side in PAGE_CATALOG/PAGE_ROUTE_RULES,
  // just missing here so it never rendered as an assignable checkbox.
  admin_boo: "Bill of Operations",
  // Oct 20 2026, user's explicit ask ("create access right permission for
  // other user - npd-store-request"): same latent bug as admin_bom_copy and
  // admin_boo above - these four were gated server-side in PAGE_CATALOG/
  // PAGE_ROUTE_RULES and linked in the nav, but missing from this map, so
  // they never rendered as assignable checkboxes and only admin/super_admin
  // (who bypass page keys) could ever open them.
  npd_store_request: "NPD Store Request",
  // Jun 2026, user's approved spec: the two tooling budget approval steps.
  npd_tooling_head_approval: "NPD Tooling Approval (NPD Head)",
  npd_tooling_management_approval: "NPD Tooling Approval (Management)",
  wip_by_stage: "WIP by Stage",
  consumption_register: "Consumption Register",
  admin_routing_builder: "Routing Builder",
  machine_master: "Machine Master",
  // Oct 23 2026, user spotted it on production: the SIXTH time a right was
  // gated server-side and linked in the nav but missing from this map, so it
  // never rendered as a checkbox and nobody could grant it. Guarded now by
  // backend/tests/test_page_catalog_parity.py.
  physical_inventory: "Physical Inventory",
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data.authenticated ? data : null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = () => {
    window.location.href = `${API}/auth/login`;
  };

  // Shop floor sign-in (Oct 26 2026) - an admin-minted username/password,
  // landing on the very same session cookie as a Microsoft sign-in.
  const passwordLogin = async (username, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/password-login`, { username, password });
      setUser(data);
      setLoading(false);
      return data;
    } catch (e) {
      const detail = e.response?.data?.detail;
      throw new Error(typeof detail === "string" ? detail : "Sign-in failed. Please try again.");
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: currentPassword, new_password: newPassword });
      await refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      throw new Error(typeof detail === "string" ? detail : "The password could not be changed.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`);
    setUser(null);
    window.location.href = "/";
  };

  // Sep 9 2026: also accepts an array of page keys - access is granted if
  // the user has ANY one of them (e.g. Supplier Portal Approvals is
  // reachable with EITHER supplier_portal_admin OR the newer
  // view-only supplier_portal_documents right).
  const hasPageAccess = (pageKey) =>
    !!user && (user.role === "super_admin" || (Array.isArray(pageKey)
      ? pageKey.some((k) => (user.allowed_pages || []).includes(k))
      : (user.allowed_pages || []).includes(pageKey)));

  const isPendingAccess = !!user && user.role !== "super_admin" && (user.allowed_pages || []).length === 0;

  return (
    <AuthContext.Provider value={{ user, loading, login, passwordLogin, changePassword, logout, refresh, hasPageAccess, isPendingAccess }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
